import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import { CheckPicker, FlexboxGrid } from 'rsuite'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { PickerInstance } from 'rsuite/esm/Picker'

import { EventKeys, sideNavItems } from './BuyerSideNav'
import useForceUpdate from '../../services/useForceUpdate'
import { useAuth } from '../../services/useAuth'
import useUrls from '../../services/useUrls'
import { NavItemProps } from '../Layouts/SideNavBar'
import useContactModal from '../Modals/hooks/useContactModal'
import { ProfileRoutes } from '../../utils/constants'
import BuyerMobileBlocker from './BuyerMobileBlocker'
import ShortcutCard from './ShortcutCard'
import HeartIcon from '../Icons/SavedProducts/HeartIcon'
import ArrowLink from '../ArrowLink'

import styles from '../../styles/BuyerPortal/MobileLayout.module.less'
import cardsCarouselStyles from '../../styles/BuyerPortal/AccountCardsCarousel.module.less'

interface ProfileMobileLayoutProps{
  showWelcome?: boolean
  header?: ReactNode
  mobileBlocker?: boolean
}

const ProfileMobileLayout: FC<ProfileMobileLayoutProps> = (props) => {
  const {
    showWelcome,
    header = null,
    mobileBlocker,
  } = props
  const [activeTab, setActiveTab] = useState('1')

  const { t } = useTranslation(('common'))
  const { t: buyerProfileTranslation } = useTranslation('buyerProfile')
  const ref = useRef<HTMLDivElement>(null)
  const pickerRef = useRef<PickerInstance>(null)
  const { pushT, urlT } = useUrls()
  const { route } = useRouter()
  const forceUpdate = useForceUpdate()
  const { user } = useAuth()

  // TODO: Add analytics context when user tracking is implemented
  const openContactModal = useContactModal()

  const containerRef = ref.current || undefined

  useEffect(() => {
    forceUpdate() // to re-render for refs to implement
  }, [])

  useEffect(() => {
    if (route.includes('/my-orders')) {
      setActiveTab(ProfileRoutes['/my-orders']?.index ?? '1')
    } else {
      setActiveTab(ProfileRoutes[route]?.index ?? '1')
    }
  }, [route])

  const handleSelect = (navItem: NavItemProps<EventKeys>) => {
    pickerRef.current?.close?.()
    setActiveTab(navItem.eventKey)
    if (navItem.isContactModel) {
      openContactModal()
    } else {
      pushT(`/${navItem.route ?? ''}`, undefined, { shallow: true })
    }
  }

  const renderMenu = () => (
    <div>
      {sideNavItems.map((item) => (
        <FlexboxGrid
          key={item.label}
          align="middle"
          className={`${styles['nav-item']} ${
            item.eventKey === activeTab ? styles.active : ''
          }`}
          onClick={() => handleSelect(item)}
        >
          <FlexboxGrid.Item className={`${styles['icon-container']} ${styles[item.classes || '']}`}>
            {item.icon && (
              <item.icon />
            )}
          </FlexboxGrid.Item>
          <FlexboxGrid.Item>
            {t(item.label)}
          </FlexboxGrid.Item>
        </FlexboxGrid>
      ))}
    </div>
  )

  const renderValue = () => {
    const value = sideNavItems.find((navItem) => navItem.eventKey === activeTab)

    return (
      <FlexboxGrid>
        <FlexboxGrid.Item>
          {value && value.icon && <value.icon />}
        </FlexboxGrid.Item>
        <FlexboxGrid.Item className="margin-left-spacer">
          {value?.label && t(value?.label)}
        </FlexboxGrid.Item>
      </FlexboxGrid>
    )
  }

  return (
    <>
      {showWelcome && (
      <h2 className="margin-bottom-spacer-double margin-top-spacer-tripple">
        {t('buyerProfile:Welcome to your Droppe account, {{name}}', { name: user?.firstname || '' })}
      </h2>
      )}
      <div className={styles['mobile-layout']}>

        {header && header}
        <div
          ref={ref}
          className="margin-bottom-spacer-tripple"
        >
          <CheckPicker
            ref={pickerRef}
            data={[]}
            renderValue={renderValue}
            value={['']}
            searchable={false}
            cleanable={false}
            container={containerRef}
            sticky
            block
            className={styles.picker}
            placement="bottomEnd"
            renderMenu={renderMenu}
          />
        </div>
        {mobileBlocker && (
        <>
          <ShortcutCard
            title={buyerProfileTranslation('My saved products')}
            icon={(
              <HeartIcon
                variant="dark"
                className={cardsCarouselStyles['heart-icon']}
              />
)}
            description={buyerProfileTranslation('View and keep an inventory of your favourite products')}
            link={(
              <ArrowLink
                href={urlT('/my-products')}
                text={buyerProfileTranslation('View saved products')}
              />
)}
          />
          <BuyerMobileBlocker />
        </>
        )}
      </div>
    </>
  )
}

export default ProfileMobileLayout
